import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Switch,
  CircularProgress,
  Divider,
  Stack,
  Autocomplete,
  TextField
} from '@mui/material';
import { Storage as StorageIcon, School, People, Dns } from '@mui/icons-material';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / Math.pow(1024, exponent)).toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

const SummaryCard = ({ icon, label, value, color }) => (
  <Card elevation={2}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" gutterBottom>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color ? `${color}.main` : undefined}>
            {value}
          </Typography>
        </Box>
        {icon}
      </Box>
    </CardContent>
  </Card>
);

const FeatureManagement = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [schools, setSchools] = useState([]); // merged: flags + storage
  const [analyticsTotals, setAnalyticsTotals] = useState(null);
  const [storageTotals, setStorageTotals] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [platformRes, storageRes, flagsRes] = await Promise.all([
        superAdminAPI.getPlatformAnalytics(),
        superAdminAPI.getStorage(),
        superAdminAPI.getFeatureFlags()
      ]);

      const platform = platformRes.data.data;
      const storage = storageRes.data.storage;
      const flagsData = flagsRes.data;
      const totalUsers = (platform.userCounts || []).reduce((sum, r) => sum + (parseInt(r.count) || 0), 0);

      setAnalyticsTotals({ totalSchools: storage.schools.length, totalUsers });
      setStorageTotals(storage.totals);
      setFeatures(flagsData.features);

      const storageBySchool = {};
      (storage.schools || []).forEach((s) => {
        storageBySchool[s.schoolId] = s;
      });

      setSchools(
        flagsData.schools.map((s) => ({
          ...s,
          storage: storageBySchool[s.schoolId] || { dbBytes: 0, fileBytes: 0, totalBytes: 0 }
        }))
      );
    } catch (error) {
      toast.error('Failed to load feature & storage data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (schoolId, featureKey, nextValue) => {
    const savingId = `${schoolId}:${featureKey}`;
    setSavingKey(savingId);

    setSchools((prev) =>
      prev.map((s) =>
        s.schoolId === schoolId ? { ...s, flags: { ...s.flags, [featureKey]: nextValue } } : s
      )
    );

    try {
      await superAdminAPI.updateFeatureFlag(schoolId, featureKey, nextValue);
    } catch (error) {
      toast.error('Failed to update feature toggle');
      console.error(error);
      setSchools((prev) =>
        prev.map((s) =>
          s.schoolId === schoolId ? { ...s, flags: { ...s.flags, [featureKey]: !nextValue } } : s
        )
      );
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const visibleSchools = selectedSchool
    ? schools.filter((s) => s.schoolId === selectedSchool.schoolId)
    : schools;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Features & Storage
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Usage at a glance, with per-school module toggles
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Total Schools"
            value={analyticsTotals?.totalSchools || 0}
            icon={<School sx={{ fontSize: 44, color: 'primary.main', opacity: 0.7 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Total Users"
            value={analyticsTotals?.totalUsers || 0}
            icon={<People sx={{ fontSize: 44, color: 'secondary.main', opacity: 0.7 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Database Data"
            value={formatBytes(storageTotals?.dbBytes)}
            color="info"
            icon={<Dns sx={{ fontSize: 44, color: 'info.main', opacity: 0.7 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Total Storage"
            value={formatBytes(storageTotals?.totalBytes)}
            color="warning"
            icon={<StorageIcon sx={{ fontSize: 44, color: 'warning.main', opacity: 0.7 }} />}
          />
        </Grid>
      </Grid>

      <Autocomplete
        options={schools}
        getOptionLabel={(s) => s.schoolName}
        value={selectedSchool}
        onChange={(e, value) => setSelectedSchool(value)}
        isOptionEqualToValue={(option, value) => option.schoolId === value.schoolId}
        renderInput={(params) => (
          <TextField {...params} label="Filter by school" placeholder="All schools" />
        )}
        sx={{ mb: 3, maxWidth: 400 }}
      />

      {visibleSchools.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No schools found</Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {visibleSchools.map((s) => (
            <Paper key={s.schoolId} sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" fontWeight="bold">
                  {s.schoolName}
                </Typography>
                <Chip
                  label={s.status}
                  color={s.status === 'Active' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Storage used: <strong>{formatBytes(s.storage.totalBytes)}</strong>
                {' '}(DB {formatBytes(s.storage.dbBytes)} + Files {formatBytes(s.storage.fileBytes)})
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Stack divider={<Divider />}>
                {features.map((f) => {
                  const savingId = `${s.schoolId}:${f.key}`;
                  return (
                    <Box
                      key={f.key}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ py: 1.5 }}
                    >
                      <Typography>{f.label}</Typography>
                      <Switch
                        checked={!!s.flags[f.key]}
                        disabled={savingKey === savingId}
                        onChange={(e) => handleToggle(s.schoolId, f.key, e.target.checked)}
                        color="primary"
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Container>
  );
};

export default FeatureManagement;
